import React from "react";
import NuMultiSelect from "../NuMultiSelect";
import { getUserDisplayTextForSelect } from "js/utils/userUtils";
import { ClassroomOverviewDTO, ClassroomTeacherDTO } from "@/stores/classroomStore_I";
import { isClassroomSynced } from "@/utils/classroomUtils";
import { ClassroomType } from "@/types/classrooms";
import { UserDetail } from "@/stores/classroomStore_I";
import { UserInfo } from "@/js/api/authApi";
import { ChakraFAIcon } from "@/components/ui/Icon";

interface TeacherTabProps {
	classroom: ClassroomOverviewDTO;
	lastSelectedTeacher: UserDetail | null;
	teacherError: string | null;
	getUsersCb: () => Promise<{ text: string; value: UserInfo }[]>;
	stagedTeachers: ClassroomTeacherDTO[];
	handleAddClassroomOwnerCb: (user: UserInfo) => void;
	handleRemoveTeacher: (user: UserInfo) => void;
	clearTeacherError: () => void;
}

export const TeacherTab: React.FC<TeacherTabProps> = ({
	classroom,
	lastSelectedTeacher,
	teacherError,
	getUsersCb,
	stagedTeachers,
	handleAddClassroomOwnerCb,
	handleRemoveTeacher,
	clearTeacherError,
}) => {
	const isClassroomSyncedStatus = isClassroomSynced(classroom);
	const isGroup = classroom.type.toLowerCase() === ClassroomType.Group;

	return (
		<div>
			{isGroup ? (
				<div className="ClassroomEdit_modalDescriptionText">Here you can view teachers that can manage this class.</div>
			) : (
				<div className="ClassroomEdit_modalDescriptionText">
					Here you can add or remove teachers that can manage this class. Teachers added here will see this class in their Classwize
					dashboard.
				</div>
			)}

			{isClassroomSyncedStatus && !isGroup ? (
				<div className="ClassroomEdit_modalDescriptionText--warning">These settings may be overridden when users are synced</div>
			) : null}

			{lastSelectedTeacher?.isTeacher === false ? (
				<div className="ClassroomEdit_validationError">
					<ChakraFAIcon icon="fa-minus-circle" variant="solid" />
					&nbsp;
					{lastSelectedTeacher.username} is not in a teacher group and cannot be added as a teacher&nbsp;
				</div>
			) : null}

			{teacherError ? (
				<div className="ClassroomEdit_validationError">
					<ChakraFAIcon icon="fa-minus-circle" variant="solid" />
					&nbsp;
					{teacherError}
				</div>
			) : null}

			<NuMultiSelect
				allItemsCb={getUsersCb}
				selectedItems={stagedTeachers.map((user) => getUserDisplayTextForSelect(user as UserInfo))}
				searchPlaceholder="Enter teacher name to see matches"
				selectedPlaceholder="No teachers selected"
				onAdd={handleAddClassroomOwnerCb}
				onRemove={handleRemoveTeacher}
				onChange={clearTeacherError}
				title="Teacher(s) of this class"
				dataCyInput="teachers-classroom_selection"
				dataCyItemsContainer="teachers-classroom_container"
				disabled={isGroup}
				minChars={3}
				dataTestId="teacher-search-input"
			/>
			<br />
		</div>
	);
};

export const renderTeacherTab = (
	classroom: ClassroomOverviewDTO,
	lastSelectedTeacher: UserDetail | null,
	teacherError: string | null,
	getUsersCb: () => Promise<{ text: string; value: UserInfo }[]>,
	stagedTeachers: ClassroomTeacherDTO[],
	handleAddClassroomOwnerCb: (user: UserInfo) => void,
	handleRemoveTeacher: (user: UserInfo) => void,
	clearTeacherError: () => void
): JSX.Element => {
	return (
		<TeacherTab
			classroom={classroom}
			lastSelectedTeacher={lastSelectedTeacher}
			teacherError={teacherError}
			getUsersCb={getUsersCb}
			stagedTeachers={stagedTeachers}
			handleAddClassroomOwnerCb={handleAddClassroomOwnerCb}
			handleRemoveTeacher={handleRemoveTeacher}
			clearTeacherError={clearTeacherError}
		/>
	);
};
